import React, { useState, useMemo } from 'react';
import { COLORS } from './SharedUI';
import { ArrowRight, ArrowLeft, Info, TrendingUp, AlertCircle, Sparkles } from 'lucide-react';
import { STRIPE_LINKS } from './data/stripeLinks';
import { trackLead } from './data/tracking';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Simulateur de valorisation — 3 méthodes pondérées
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//
// MÉTHODES :
//  1. Multiples sectoriels (EBITDA × multiple secteur)
//  2. DCF simplifié (flux actualisés sur 5 ans + valeur terminale)
//  3. Actif Net Réévalué (fonds propres + plus-values latentes)
//
// PONDÉRATION DYNAMIQUE selon secteur (industrie = + ANR, tech = + DCF, services = + multiples)
//
// AJUSTEMENTS :
//  - Retraitements EBITDA (salaire dirigeant excessif + charges perso)
//  - Décote/prime taille (TPE, PME, ETI)
//  - Bonus croissance
//  - Décote dépendance dirigeant

const fmtK = (n) => {
  if (!isFinite(n) || isNaN(n)) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(2).replace('.', ',')} M€`;
  if (n >= 1000) return `${Math.round(n / 1000).toLocaleString('fr-FR')} k€`;
  return `${Math.round(n).toLocaleString('fr-FR')} €`;
};

// 14 secteurs avec multiples EBITDA réalistes + pondération méthodes
const SECTEURS = {
  'services-b2b':    { label: 'Services B2B (conseil, agence)', mult: { min: 4.5, max: 7.5 }, pond: { mult: 0.65, dcf: 0.30, anr: 0.05 } },
  'tech-saas':       { label: 'Tech / SaaS B2B',                mult: { min: 6, max: 12 },    pond: { mult: 0.40, dcf: 0.55, anr: 0.05 } },
  'commerce':        { label: 'Commerce de détail / e-commerce', mult: { min: 3, max: 5.5 },  pond: { mult: 0.55, dcf: 0.20, anr: 0.25 } },
  'restauration':    { label: 'Restauration / CHR',             mult: { min: 3, max: 5 },     pond: { mult: 0.55, dcf: 0.15, anr: 0.30 } },
  'btp':             { label: 'BTP / Artisanat',                mult: { min: 3, max: 5.5 },   pond: { mult: 0.50, dcf: 0.20, anr: 0.30 } },
  'sante':           { label: 'Santé / Médical / Bien-être',    mult: { min: 5, max: 8.5 },   pond: { mult: 0.60, dcf: 0.30, anr: 0.10 } },
  'industrie':       { label: 'Industrie / Manufacture',        mult: { min: 4, max: 6.5 },   pond: { mult: 0.40, dcf: 0.25, anr: 0.35 } },
  'immobilier':      { label: 'Immobilier / Agence',            mult: { min: 4, max: 7 },     pond: { mult: 0.40, dcf: 0.25, anr: 0.35 } },
  'agroalimentaire': { label: 'Agroalimentaire',                mult: { min: 4, max: 7 },     pond: { mult: 0.50, dcf: 0.25, anr: 0.25 } },
  'hotellerie':      { label: 'Hôtellerie',                     mult: { min: 6, max: 10 },    pond: { mult: 0.35, dcf: 0.25, anr: 0.40 } },
  'transport':       { label: 'Transport / Logistique',         mult: { min: 3, max: 5 },     pond: { mult: 0.35, dcf: 0.20, anr: 0.45 } },
  'finance':         { label: 'Finance / Assurance',            mult: { min: 5, max: 8 },     pond: { mult: 0.55, dcf: 0.35, anr: 0.10 } },
  'education':       { label: 'Éducation / Formation',          mult: { min: 4, max: 7 },     pond: { mult: 0.55, dcf: 0.30, anr: 0.15 } },
  'autres':          { label: 'Autres services',                mult: { min: 3.5, max: 6 },   pond: { mult: 0.55, dcf: 0.25, anr: 0.20 } },
};

const STEPS = [
  { id: 'profil',     label: 'Profil',     short: '1' },
  { id: 'financier',  label: 'Finances',   short: '2' },
  { id: 'perspectives', label: 'Perspectives', short: '3' },
  { id: 'resultat',   label: 'Résultat',   short: '✓' },
];

export default function Simulator() {
  const [step, setStep] = useState(0);

  // ━━━━━━ État du simulateur ━━━━━━
  const [data, setData] = useState({
    // Profil
    secteur: 'services-b2b',
    anciennete: 8,       // années d'existence
    nbSalaries: 8,
    // Finances
    ca: 800000,
    ebitdaConnu: 'oui',  // 'oui' / 'non' → si non, on calcule depuis le résultat
    ebitda: 120000,
    resultatNet: 60000,
    amortissements: 30000,
    interets: 10000,
    impots: 18000,
    // Retraitements
    salaireRetraitement: 20000,  // salaire dirigeant > marché ou < marché (peut être 0)
    chargesPersoEnFrais: 5000,   // dépenses perso passées en charges entreprise
    // Bilan
    fondsPropres: 250000,
    detteFinanciere: 80000,
    tresorerie: 60000,
    plusValuesLatentes: 0,        // immobilier, brevets sous-évalués au bilan
    // Perspectives
    croissanceAnnuelle: 12,       // % CA prévu
    margeCible: 14,              // % EBITDA cible dans 3 ans
    dependanceDirigeant: 'moyenne', // 'faible' / 'moyenne' / 'forte'
  });

  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));
  // Scroll vers le haut de la section simulateur (pas le top de la page) — sinon l'utilisateur a l'impression d'être renvoyé à l'accueil.
  // setTimeout pour attendre le re-render React avant de scroller (sinon le scroll s'applique sur l'ancien DOM).
  const scrollToSimulator = () => {
    setTimeout(() => {
      const el = document.getElementById('simulateur');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
  };
  const next = () => { setStep(s => Math.min(STEPS.length - 1, s + 1)); scrollToSimulator(); };
  const prev = () => { setStep(s => Math.max(0, s - 1)); scrollToSimulator(); };

  // ━━━━━━ CALCULS ━━━━━━
  const result = useMemo(() => {
    const sec = SECTEURS[data.secteur];

    // 1. EBITDA retraité (méthode banquier)
    const ebitdaBrut = data.ebitdaConnu === 'oui'
      ? data.ebitda
      : data.resultatNet + data.amortissements + data.interets + data.impots;
    const ebitdaRetraite = ebitdaBrut + data.salaireRetraitement + data.chargesPersoEnFrais;

    // 2. Dette nette = dette financière − trésorerie
    const detteNette = Math.max(0, data.detteFinanciere - data.tresorerie);

    // 3. Marge EBITDA actuelle
    const margeEbitda = data.ca > 0 ? (ebitdaRetraite / data.ca) * 100 : 0;

    // ━━━━━━ Ajustements communs ━━━━━━
    // Bonus croissance
    let bonusCroissance = 1;
    if (data.croissanceAnnuelle >= 30) bonusCroissance = 1.20;
    else if (data.croissanceAnnuelle >= 20) bonusCroissance = 1.12;
    else if (data.croissanceAnnuelle >= 10) bonusCroissance = 1.05;
    else if (data.croissanceAnnuelle < 0) bonusCroissance = 0.80;

    // Décote dépendance dirigeant
    const decoteDirigeant = data.dependanceDirigeant === 'forte' ? 0.75 : data.dependanceDirigeant === 'moyenne' ? 0.90 : 1.0;

    // Décote/prime taille
    let primeTaille = 1;
    if (data.ca < 200000) primeTaille = 0.80;       // TPE
    else if (data.ca < 1000000) primeTaille = 0.95; // petite PME
    else if (data.ca < 5000000) primeTaille = 1;    // PME
    else if (data.ca < 20000000) primeTaille = 1.10; // grosse PME
    else primeTaille = 1.20;                         // ETI

    // ━━━━━━ MÉTHODE 1 : Multiples sectoriels EBITDA ━━━━━━
    const multAjust = bonusCroissance * decoteDirigeant * primeTaille;
    const evMultMin = ebitdaRetraite * sec.mult.min * multAjust;
    const evMultMax = ebitdaRetraite * sec.mult.max * multAjust;
    const evMultCentral = (evMultMin + evMultMax) / 2;
    const equityMultMin = Math.max(0, evMultMin - detteNette);
    const equityMultMax = Math.max(0, evMultMax - detteNette);
    const equityMultCentral = Math.max(0, evMultCentral - detteNette);

    // ━━━━━━ MÉTHODE 2 : DCF simplifié 5 ans + valeur terminale ━━━━━━
    const wacc = 0.11;  // 11% par défaut (taux d'actualisation PME)
    const gPerpetuel = 0.025; // 2,5% croissance perpétuelle
    const cashFlows = [];
    let ca = data.ca;
    let margeProjetee = margeEbitda / 100;
    const margeCibleDecimal = data.margeCible / 100;
    const g = data.croissanceAnnuelle / 100;

    for (let i = 1; i <= 5; i++) {
      ca = ca * (1 + g);
      // Convergence linéaire de la marge actuelle vers la marge cible sur 3 ans
      const progress = Math.min(1, i / 3);
      const margeYear = margeProjetee + (margeCibleDecimal - margeProjetee) * progress;
      const fcf = ca * margeYear * 0.70; // 70% de l'EBITDA = approximation cash flow disponible (après impôts, BFR, CAPEX)
      cashFlows.push(fcf / Math.pow(1 + wacc, i));
    }
    const sommeFlux = cashFlows.reduce((a, b) => a + b, 0);
    // Valeur terminale = CF année 5 normalisée / (WACC − g perpétuel), actualisée
    const cf5Normalise = ca * margeCibleDecimal * 0.70;
    const valTerminale = (cf5Normalise * (1 + gPerpetuel)) / (wacc - gPerpetuel) / Math.pow(1 + wacc, 5);
    const evDcfBrut = sommeFlux + valTerminale;
    const evDcfAjuste = evDcfBrut * decoteDirigeant; // on n'applique pas la prime taille au DCF (déjà dans la croissance)
    const evDcfMin = evDcfAjuste * 0.85;
    const evDcfMax = evDcfAjuste * 1.15;
    const equityDcfMin = Math.max(0, evDcfMin - detteNette);
    const equityDcfMax = Math.max(0, evDcfMax - detteNette);
    const equityDcfCentral = Math.max(0, evDcfAjuste - detteNette);

    // ━━━━━━ MÉTHODE 3 : ANR (Actif Net Réévalué) ━━━━━━
    const anrBrut = data.fondsPropres + data.plusValuesLatentes;
    const equityAnrMin = anrBrut * 0.85;
    const equityAnrMax = anrBrut * 1.15;
    const equityAnrCentral = anrBrut;

    // ━━━━━━ VALEUR CENTRALE PONDÉRÉE ━━━━━━
    const equityCentralPondere =
      equityMultCentral * sec.pond.mult +
      equityDcfCentral * sec.pond.dcf +
      equityAnrCentral * sec.pond.anr;

    const equityMinPondere =
      equityMultMin * sec.pond.mult +
      equityDcfMin * sec.pond.dcf +
      equityAnrMin * sec.pond.anr;

    const equityMaxPondere =
      equityMultMax * sec.pond.mult +
      equityDcfMax * sec.pond.dcf +
      equityAnrMax * sec.pond.anr;

    // ━━━━━━ Score de confiance ━━━━━━
    // Plus l'écart entre méthodes est faible, plus la valorisation est robuste
    const valeurs = [equityMultCentral, equityDcfCentral, equityAnrCentral].filter(v => v > 0);
    const max = Math.max(...valeurs);
    const min = Math.min(...valeurs);
    const ecartRel = max > 0 ? (max - min) / max : 1;
    let confidence = 'élevée';
    let confidenceColor = COLORS.green;
    if (ecartRel > 0.6) { confidence = 'faible'; confidenceColor = '#8B2C1C'; }
    else if (ecartRel > 0.35) { confidence = 'moyenne'; confidenceColor = '#A87A1C'; }

    // ━━━━━━ Recommandations ━━━━━━
    const recos = [];
    if (data.dependanceDirigeant === 'forte') recos.push("Réduire votre dépendance opérationnelle (recruter un n°2, déléguer commercial) → +10-25% sur la valeur");
    if (margeEbitda < 8) recos.push(`Améliorer la marge EBITDA (actuellement ${margeEbitda.toFixed(1)}%, viser >12%) → impact direct sur les multiples`);
    if (data.salaireRetraitement < 5000 && data.ca > 500000) recos.push("Vérifier le retraitement du salaire dirigeant — souvent sous-estimé (économie potentielle 20-50K€ d'EBITDA reconnu)");
    if (detteNette > ebitdaRetraite * 3) recos.push("Réduire la dette financière (ratio dette/EBITDA > 3x) avant cession");
    if (data.croissanceAnnuelle < 5 && data.ca > 1000000) recos.push("Démontrer une dynamique de croissance (objectif > 8%/an) → bonus 5-12% sur le multiple");
    if (recos.length === 0) recos.push("Votre profil financier est solide. Pour maximiser, présentez un dossier propre avec 3 ans de comptes retraités.");

    return {
      ebitdaBrut, ebitdaRetraite, margeEbitda, detteNette,
      mult: { min: equityMultMin, max: equityMultMax, central: equityMultCentral, ev: evMultCentral },
      dcf: { min: equityDcfMin, max: equityDcfMax, central: equityDcfCentral, ev: evDcfAjuste, sommeFlux, valTerminale },
      anr: { min: equityAnrMin, max: equityAnrMax, central: equityAnrCentral },
      pondere: { min: equityMinPondere, max: equityMaxPondere, central: equityCentralPondere },
      confidence, confidenceColor,
      ponderation: sec.pond,
      recos,
      bonusCroissance, decoteDirigeant, primeTaille,
    };
  }, [data]);

  const validForNext = () => {
    if (step === 0) return data.nbSalaries >= 0;
    if (step === 1) return data.ca > 0 && (data.ebitdaConnu === 'oui' ? data.ebitda > 0 : data.resultatNet !== '');
    if (step === 2) return true;
    return true;
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // RENDU
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div style={{ background: 'white', borderRadius: '20px', boxShadow: '0 8px 32px rgba(10,25,41,0.08)', border: '1px solid #D8D3C5', overflow: 'hidden' }}>

      {/* Stepper */}
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #D8D3C5', background: '#F4EFE4' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              {/* Plus d'opacity globale ici : appliquée au texte, elle écrasait le
                  contraste des étapes à venir à 1,72:1 (audit 2026-08-15). Aucune
                  valeur d'opacité ne permet d'atteindre les 4,5:1 requis — l'état
                  « pas encore atteint » passe donc par la couleur, pas la transparence. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: i < step ? COLORS.green : i === step ? COLORS.blue : '#e5e7eb',
                  color: i <= step ? 'white' : '#4F5560',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: '700',
                }}>{i < step ? '✓' : s.short}</div>
                <span style={{ fontSize: '13px', fontWeight: '600', color: i === step ? COLORS.blue : '#5F6675' }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div style={{ width: '24px', height: '2px', background: i < step ? COLORS.green : '#e5e7eb' }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ padding: '28px' }}>

        {/* ━━━━━━━━━━ ÉTAPE 1 : PROFIL ━━━━━━━━━━ */}
        {step === 0 && (
          <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0a1929', marginBottom: '4px' }}>Votre profil d'entreprise</h3>
            <p style={{ fontSize: '13.5px', color: '#666', marginBottom: '20px' }}>3 questions rapides pour adapter la méthodologie à votre secteur</p>

            <Field
              label="Secteur d'activité"
              info="Le secteur détermine les multiples sectoriels appliqués (combien vaut votre EBITDA × X) et la pondération entre les 3 méthodes (Multiples, DCF, ANR). Par exemple un SaaS sera plus valorisé sur sa croissance (DCF), un commerce sur ses multiples sectoriels."
              hint="Détermine les multiples sectoriels et la pondération des méthodes">
              <select aria-label="Secteur d’activité" value={data.secteur} onChange={e => upd('secteur', e.target.value)} style={selectStyle}>
                {Object.entries(SECTEURS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>

            <SliderField label="Ancienneté de l'entreprise" value={data.anciennete} min={0} max={50} step={1}
              format={(n) => `${n} an${n > 1 ? 's' : ''}`}
              onChange={v => upd('anciennete', v)}
              hint="Une entreprise > 3 ans est mieux valorisée (preuve de viabilité)" />

            <SliderField label="Nombre de salariés" value={data.nbSalaries} min={0} max={250} step={1}
              format={(n) => n === 0 ? 'Solo / aucun salarié' : `${n} salarié${n > 1 ? 's' : ''}`}
              onChange={v => upd('nbSalaries', v)}
              hint="Une équipe stable rassure le repreneur — impact direct sur la valorisation" />
          </div>
        )}

        {/* ━━━━━━━━━━ ÉTAPE 2 : FINANCES ━━━━━━━━━━ */}
        {step === 1 && (
          <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0a1929', marginBottom: '4px' }}>💰 Vos données financières</h3>
            <p style={{ fontSize: '13.5px', color: '#666', marginBottom: '20px' }}>Plus précis = meilleure estimation. Vous pouvez ajuster les retraitements pour révéler la valeur cachée.</p>

            <SliderField label="Chiffre d'affaires annuel HT" value={data.ca} min={50000} max={20000000} step={10000}
              format={fmtK} onChange={v => upd('ca', v)} hint="CA du dernier exercice clos" />

            <Field
              label="Connaissez-vous votre EBITDA ?"
              info="EBITDA = Excédent Brut d'Exploitation (Earnings Before Interest, Taxes, Depreciation, Amortization). C'est le bénéfice avant déduction des intérêts, impôts et amortissements. Bonne mesure de la rentabilité opérationnelle pure."
              hint="Si non, on le calcule depuis votre résultat net">
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['oui', '✅ Oui, je le saisis'], ['non', '❌ Non, calculer pour moi']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => upd('ebitdaConnu', v)} style={{
                    flex: 1, padding: '10px 14px', fontSize: '13px', fontWeight: '600',
                    background: data.ebitdaConnu === v ? COLORS.blue : 'white',
                    color: data.ebitdaConnu === v ? 'white' : '#444',
                    border: `1.5px solid ${data.ebitdaConnu === v ? COLORS.blue : '#D8D3C5'}`,
                    borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit',
                  }}>{lbl}</button>
                ))}
              </div>
            </Field>

            {data.ebitdaConnu === 'oui' ? (
              <SliderField label="EBITDA annuel" value={data.ebitda} min={0} max={data.ca} step={1000}
                format={fmtK} onChange={v => upd('ebitda', v)}
                info="L'EBITDA correspond à votre Excédent Brut d'Exploitation. Pour une PME, marge typique entre 10% (commerce) et 25% (services / SaaS). Si vous ne savez pas, choisissez 'Non' à la question précédente."
                hint="EBE = Excédent Brut d'Exploitation. Marge typique : 10-25% du CA" />
            ) : (
              <>
                <SliderField label="Résultat net" value={data.resultatNet} min={-100000} max={data.ca / 2} step={1000}
                  format={fmtK} onChange={v => upd('resultatNet', v)} />
                <SliderField label="Dotations aux amortissements" value={data.amortissements} min={0} max={data.ca / 4} step={500}
                  format={fmtK} onChange={v => upd('amortissements', v)}
                  info="L'amortissement traduit l'usure annuelle de vos actifs (machines, matériel, véhicules…). Visible sur votre compte de résultat (poste 681). Ce n'est pas une sortie de cash, donc on l'ajoute pour calculer l'EBITDA."
                  hint="Poste 681 du compte de résultat" />
                <SliderField label="Charges financières / intérêts" value={data.interets} min={0} max={data.ca / 10} step={500}
                  format={fmtK} onChange={v => upd('interets', v)}
                  info="Intérêts payés sur vos emprunts dans l'année (banques, leasing financier, comptes courants associés). Poste 661 du compte de résultat."
                  hint="Intérêts d'emprunt annuels (poste 661)" />
                <SliderField label="Impôt sur les sociétés payé" value={data.impots} min={0} max={data.ca / 5} step={500}
                  format={fmtK} onChange={v => upd('impots', v)}
                  info="Impôt sur les Sociétés (IS) effectivement payé. Pour une PME, taux normal de 25% sur le résultat fiscal." />
              </>
            )}

            {/* Retraitements (essentiel à la valorisation) */}
            <div style={{ padding: '14px 16px', background: '#fef3c7', borderRadius: '10px', borderLeft: '4px solid #A87A1C', marginBottom: '14px' }}>
              <strong style={{ fontSize: '13px', color: '#92400e' }}>💡 Retraitements EBITDA — étape clé !</strong>
              <p style={{ fontSize: '12px', color: '#78350f', margin: '4px 0 0', lineHeight: 1.5 }}>
                Les retraitements révèlent le VRAI EBITDA. Ils peuvent ajouter <strong>+50K€ ou plus</strong> à votre valeur.
              </p>
            </div>

            <SliderField label="Salaire dirigeant excessif (vs marché)" value={data.salaireRetraitement} min={0} max={200000} step={1000}
              format={fmtK} onChange={v => upd('salaireRetraitement', v)}
              info="Différence entre votre salaire actuel et un salaire de marché pour ce poste. Ex : vous vous versez 100K€/an mais un salarié à votre poste serait à 70K€ → 30K€ à retraiter en + (booste l'EBITDA). Si vous vous sous-payez, mettre la différence inverse (négative)."
              hint="Si vous vous versez 100K€ alors qu'un salarié à ce poste serait à 70K€ → retraitement de 30K€" />

            <SliderField label="Charges personnelles passées en frais entreprise" value={data.chargesPersoEnFrais} min={0} max={100000} step={500}
              format={fmtK} onChange={v => upd('chargesPersoEnFrais', v)}
              info="Tout ce que vous passez en charges de l'entreprise mais qui relève du personnel : voiture luxe non strictement utile, repas privés, voyages 'salons' = vacances déguisées, abonnements perso, etc. À réintégrer pour montrer le vrai EBITDA."
              hint="Voiture personnelle, restos, abonnements perso… (à réintégrer à l'EBITDA)" />

            {/* Bilan */}
            <div style={{ height: '1px', background: '#D8D3C5', margin: '20px 0 16px' }} />
            <p style={{ fontSize: '12.5px', color: '#666', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>📊 Éléments de bilan</p>

            <SliderField label="Fonds propres (capital + réserves + résultats)" value={data.fondsPropres} min={0} max={5000000} step={5000}
              format={fmtK} onChange={v => upd('fondsPropres', v)}
              info="Capital social + Réserves + Résultats accumulés non distribués. Visible au bilan dans le 'haut de bilan' (Total I des capitaux propres). Mesure la richesse propre de l'entreprise."
              hint="Total des capitaux propres au bilan (utile pour la méthode ANR)" />

            <SliderField label="Dette financière totale" value={data.detteFinanciere} min={0} max={5000000} step={5000}
              format={fmtK} onChange={v => upd('detteFinanciere', v)}
              info="Tous vos emprunts bancaires en cours (capital restant dû) + leasing financier + comptes courants associés. Pas les fournisseurs ni les charges sociales courantes."
              hint="Emprunts bancaires + comptes courants associés (postes 16, 519, 455)" />

            <SliderField label="Trésorerie disponible" value={data.tresorerie} min={0} max={5000000} step={5000}
              format={fmtK} onChange={v => upd('tresorerie', v)}
              info="Cash en banque + placements liquides (livrets, SICAV monétaires...). C'est l'argent immédiatement disponible. NE PAS inclure les comptes clients (encours)."
              hint="Cash en banque + placements liquides" />
          </div>
        )}

        {/* ━━━━━━━━━━ ÉTAPE 3 : PERSPECTIVES ━━━━━━━━━━ */}
        {step === 2 && (
          <div style={{ animation: 'fadeUp 0.3s ease-out' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0a1929', marginBottom: '4px' }}>📈 Perspectives & ajustements</h3>
            <p style={{ fontSize: '13.5px', color: '#666', marginBottom: '20px' }}>Ces éléments alimentent la méthode DCF et appliquent les bonus/décotes</p>

            <SliderField label="Croissance annuelle prévue (3 ans)" value={data.croissanceAnnuelle} min={-10} max={60} step={1}
              format={(n) => `${n > 0 ? '+' : ''}${n} %`} onChange={v => upd('croissanceAnnuelle', v)}
              info="Taux de croissance moyen de votre CA prévu sur 3 ans. Soyez réaliste : une PME en croissance forte fait +15-30%/an, en stable +0-10%, en difficulté <0%."
              hint="Croissance > 20% = bonus 12%, > 10% = bonus 5%. Négatif = décote 20%" />

            <SliderField label="Marge EBITDA cible à 3 ans" value={data.margeCible} min={3} max={45} step={1}
              format={(n) => `${n} %`} onChange={v => upd('margeCible', v)}
              info="Marge EBITDA que vous visez à atteindre dans 3 ans (% du CA). Référence : services B2B 15-25%, SaaS 25-40%, commerce 5-12%, BTP 8-15%, industrie 10-18%."
              hint="Objectif réaliste de marge dans 3 ans (utilisé dans le DCF)" />

            <Field
              label="Dépendance au dirigeant"
              info="Mesure à quel point l'entreprise tourne SANS vous. Si vous êtes seul à signer les contrats, gérer les clients clés, faire la stratégie → forte dépendance → décote 25%. Si l'équipe est autonome → bonus implicite."
              hint="Si vous êtes indispensable, le repreneur applique une décote">
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  ['faible', '✅ Faible', "L'équipe tourne sans moi"],
                  ['moyenne', '🟡 Moyenne', "Je gère les gros sujets"],
                  ['forte', '⚠️ Forte', 'Tout passe par moi'],
                ].map(([v, lbl, desc]) => (
                  <button key={v} type="button" onClick={() => upd('dependanceDirigeant', v)} style={{
                    flex: 1, padding: '10px 12px',
                    background: data.dependanceDirigeant === v ? COLORS.blue : 'white',
                    color: data.dependanceDirigeant === v ? 'white' : '#444',
                    border: `1.5px solid ${data.dependanceDirigeant === v ? COLORS.blue : '#D8D3C5'}`,
                    borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{lbl}</div>
                    <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>{desc}</div>
                  </button>
                ))}
              </div>
            </Field>

            <SliderField label="Plus-values latentes (immobilier, marque, brevets)" value={data.plusValuesLatentes} min={0} max={5000000} step={10000}
              format={fmtK} onChange={v => upd('plusValuesLatentes', v)}
              info="Différence entre la VRAIE valeur de vos actifs (immobilier, marque, brevets, parts dans d'autres sociétés) et leur valeur comptable au bilan (souvent sous-évaluée car amortie). Ex : un local acheté 200K€ il y a 15 ans peut valoir 400K€ aujourd'hui = +200K€ de plus-value latente."
              hint="Différence entre la valeur réelle et la valeur comptable de vos actifs (uniquement pour ANR)" />
          </div>
        )}

        {/* ━━━━━━━━━━ ÉTAPE 4 : RÉSULTAT ━━━━━━━━━━ */}
        {step === 3 && <ResultView data={data} result={result} />}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #D8D3C5' }}>
          <button type="button" onClick={prev} disabled={step === 0} style={{
            padding: '11px 20px', fontSize: '13.5px', fontWeight: '600',
            background: 'white', color: step === 0 ? '#aaa' : '#444',
            border: '1px solid #D8D3C5', borderRadius: '8px',
            cursor: step === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            <ArrowLeft size={14} /> Retour
          </button>

          <div style={{ fontSize: '12px', color: '#5F6675' }}>{`Étape ${step + 1}/${STEPS.length}`}</div>

          {step < STEPS.length - 1 ? (
            <button type="button" onClick={next} disabled={!validForNext()} className={validForNext() ? 'cta-glow' : ''} style={{
              padding: '11px 22px', fontSize: '14px', fontWeight: '700',
              background: validForNext() ? `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.dark} 100%)` : '#d1d5db',
              color: 'white', border: 'none', borderRadius: '8px',
              cursor: validForNext() ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}>
              {step === STEPS.length - 2 ? 'Voir le résultat' : 'Suivant'} <ArrowRight size={14} />
            </button>
          ) : (
            <button type="button" onClick={() => setStep(0)} style={{
              padding: '11px 20px', fontSize: '13.5px', fontWeight: '600',
              background: 'white', color: COLORS.blue,
              border: `1.5px solid ${COLORS.blue}30`, borderRadius: '8px',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
              🔄 Nouvelle simulation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COMPOSANT : Vue résultat
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
function ResultView({ data, result }) {
  // États pour la capture email (envoi du rapport simulé via Brevo)
  const [askName, setAskName] = useState('');
  const [askEmail, setAskEmail] = useState('');
  const [askSubmitting, setAskSubmitting] = useState(false);
  const [askSent, setAskSent] = useState(false);
  const [askError, setAskError] = useState(null);
  const [showAskForm, setShowAskForm] = useState(false);

  // Bar chart simple en SVG
  const maxBar = Math.max(result.mult.max, result.dcf.max, result.anr.max, result.pondere.max);

  const methodes = [
    { name: 'Multiples EBITDA', color: COLORS.blue, central: result.mult.central, min: result.mult.min, max: result.mult.max, pond: result.ponderation.mult, desc: `EBITDA retraité (${fmtK(result.ebitdaRetraite)}) × multiple sectoriel, ajusté` },
    { name: 'DCF actualisé', color: '#0A1929', central: result.dcf.central, min: result.dcf.min, max: result.dcf.max, pond: result.ponderation.dcf, desc: 'Flux de trésorerie actualisés sur 5 ans + valeur terminale (WACC 11%)' },
    { name: 'Actif Net Réévalué', color: '#A87A1C', central: result.anr.central, min: result.anr.min, max: result.anr.max, pond: result.ponderation.anr, desc: 'Fonds propres comptables + plus-values latentes (méthode patrimoniale)' },
  ];

  // Soumission du formulaire d'envoi de la simulation par email (via Brevo)
  // Le détail du rapport est généré côté serveur dans /api/simulator-report (HTML pro)
  const handleAskSubmit = async (e) => {
    e.preventDefault();
    if (askSubmitting) return;
    setAskSubmitting(true); setAskError(null);
    try {
      const r = await fetch('/api/simulator-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: askName,
          email: askEmail,
          // Profil entreprise complet pour reconstruction du rapport HTML
          profile: {
            secteur: SECTEURS[data.secteur]?.label || data.secteur,
            anciennete: data.anciennete,
            nbSalaries: data.nbSalaries,
            ca: data.ca,
            fondsPropres: data.fondsPropres,
            salaireRetraitement: data.salaireRetraitement,
            chargesPersoEnFrais: data.chargesPersoEnFrais,
          },
          // Résultats des 3 méthodes + pondération + indicateurs clés
          results: {
            ebitdaBrut: result.ebitdaBrut,
            ebitdaRetraite: result.ebitdaRetraite,
            margeEbitda: result.margeEbitda,
            detteNette: result.detteNette,
            mult: result.mult,
            dcf: result.dcf,
            anr: result.anr,
            pondere: result.pondere,
            ponderation: result.ponderation,
            confidence: result.confidence,
          },
        }),
      });
      const data2 = await r.json().catch(() => ({}));
      if (r.ok && data2.ok) {
        await trackLead({
          source: 'simulateur',
          value: 3,
          email: askEmail,
          name: askName,
        });
        setAskSent(true);
      } else {
        setAskError(data2.error || "Échec de l'envoi. Réessayez.");
      }
    } catch (err) {
      setAskError("Erreur réseau. Réessayez ou écrivez à contact@finanalyse.fr");
    } finally { setAskSubmitting(false); }
  };

  return (
    <div style={{ animation: 'fadeUp 0.3s ease-out' }}>

      {/* Résultat central */}
      <div style={{ padding: '24px', background: `linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.blue} 100%)`, borderRadius: '14px', color: 'white', textAlign: 'center', marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', opacity: 0.85, fontWeight: '700', letterSpacing: '1px', marginBottom: '8px' }}>VALEUR CENTRALE ESTIMÉE · MOYENNE PONDÉRÉE</div>
        <div style={{ fontSize: 'clamp(36px, 7vw, 56px)', fontWeight: '800', letterSpacing: '-0.02em', lineHeight: 1 }}>
          {fmtK(result.pondere.central)}
        </div>
        <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '8px' }}>
          Fourchette : <strong>{fmtK(result.pondere.min)} – {fmtK(result.pondere.max)}</strong>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', padding: '4px 12px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: result.confidenceColor, boxShadow: `0 0 6px ${result.confidenceColor}` }} />
          Confiance : {result.confidence} (écart entre méthodes)
        </div>
      </div>

      {/* Bar chart comparaison des 3 méthodes */}
      <div style={{ padding: '20px', background: '#F4EFE4', borderRadius: '12px', marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#0a1929', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Sparkles size={16} style={{ color: COLORS.blue }} /> Comparaison des 3 méthodes
        </h4>
        {methodes.map(m => {
          const wMin = maxBar > 0 ? (m.min / maxBar) * 100 : 0;
          const wMax = maxBar > 0 ? (m.max / maxBar) * 100 : 0;
          const wCenter = maxBar > 0 ? (m.central / maxBar) * 100 : 0;
          return (
            <div key={m.name} style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                <span style={{ fontSize: '12.5px', fontWeight: '700', color: '#0a1929' }}>
                  {m.name} <span style={{ fontSize: '10.5px', color: '#5F6675', fontWeight: '500' }}>· pondération {(m.pond * 100).toFixed(0)}%</span>
                </span>
                <span style={{ fontSize: '13px', fontWeight: '800', color: m.color }}>{fmtK(m.central)}</span>
              </div>
              <div style={{ position: 'relative', height: '10px', background: '#e5e7eb', borderRadius: '5px', overflow: 'visible' }}>
                <div style={{ position: 'absolute', left: `${wMin}%`, width: `${Math.max(1, wMax - wMin)}%`, top: 0, bottom: 0, background: `${m.color}40`, borderRadius: '5px' }} />
                <div style={{ position: 'absolute', left: `${wCenter}%`, width: '3px', top: '-2px', bottom: '-2px', background: m.color, borderRadius: '2px' }} />
              </div>
              <div style={{ fontSize: '10.5px', color: '#5F6675', marginTop: '3px' }}>
                Fourchette : {fmtK(m.min)} – {fmtK(m.max)} · {m.desc}
              </div>
            </div>
          );
        })}
      </div>

      {/* Détail calcul */}
      <div style={{ padding: '16px 18px', background: 'white', border: '1px solid #D8D3C5', borderRadius: '10px', marginBottom: '20px' }}>
        <details>
          <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: '#0a1929', userSelect: 'none' }}>
            Voir le détail du calcul
          </summary>
          <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#444', lineHeight: '1.8' }}>
            <div><strong>EBITDA brut :</strong> {fmtK(result.ebitdaBrut)}</div>
            <div><strong>+ Retraitements :</strong> {fmtK(data.salaireRetraitement + data.chargesPersoEnFrais)} (salaire excessif + charges perso)</div>
            <div><strong>= EBITDA retraité :</strong> <span style={{ color: COLORS.green, fontWeight: '700' }}>{fmtK(result.ebitdaRetraite)}</span> (marge {result.margeEbitda.toFixed(1)}%)</div>
            <div style={{ marginTop: '8px' }}><strong>Dette nette :</strong> {fmtK(result.detteNette)} (= dette financière − trésorerie)</div>
            <div style={{ marginTop: '8px' }}><strong>Bonus croissance :</strong> ×{result.bonusCroissance.toFixed(2)}</div>
            <div><strong>Décote dépendance :</strong> ×{result.decoteDirigeant.toFixed(2)}</div>
            <div><strong>Prime/décote taille :</strong> ×{result.primeTaille.toFixed(2)}</div>
            <div style={{ marginTop: '8px', padding: '8px 10px', background: '#F4EFE4', borderRadius: '6px' }}>
              <strong>Pondération secteur :</strong> Multiples {(result.ponderation.mult*100).toFixed(0)}% · DCF {(result.ponderation.dcf*100).toFixed(0)}% · ANR {(result.ponderation.anr*100).toFixed(0)}%
            </div>
          </div>
        </details>
      </div>

      {/* Recommandations */}
      <div style={{ padding: '18px 20px', background: '#fef3c7', borderRadius: '12px', borderLeft: '4px solid #A87A1C', marginBottom: '20px' }}>
        {/* h3 et non h4 : le titre de section qui précède est un h2, sauter un
            niveau casse la navigation au lecteur d'écran (audit 2026-08-15). */}
        <h3 style={{ fontSize: '13px', fontWeight: '800', color: '#92400e', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TrendingUp size={15} /> Recommandations pour augmenter votre valorisation
        </h3>
        <ul style={{ margin: 0, paddingLeft: '20px' }}>
          {result.recos.map((r, i) => (
            <li key={i} style={{ fontSize: '12.5px', color: '#78350f', marginBottom: '5px', lineHeight: 1.55 }}>{r}</li>
          ))}
        </ul>
      </div>

      {/* Disclaimer */}
      <div style={{ padding: '12px 14px', background: '#F4EFE4', borderRadius: '8px', fontSize: '11.5px', color: '#666', marginBottom: '18px', display: 'flex', gap: '8px' }}>
        <AlertCircle size={16} style={{ color: '#5F6675', flexShrink: 0, marginTop: '1px' }} />
        <div>
          <strong>Estimation indicative.</strong> Pour une valorisation officielle (cession, levée de fonds, banquier, pacte d'associés), il vous faut un <strong>rapport pro de 20 pages</strong> basé sur l'analyse de vos comptes réels, des comparables transactionnels et des ajustements personnalisés. Méthodes DCF + Multiples + ANR, livré sous 48h.
        </div>
      </div>

      {/* Bandeau pitch rapport pro */}
      <div style={{ padding: '18px 20px', background: `linear-gradient(135deg, ${COLORS.green}10 0%, ${COLORS.blue}10 100%)`, border: `1.5px solid ${COLORS.green}30`, borderRadius: '12px', marginBottom: '16px' }}>
        <div style={{ fontSize: '12.5px', fontWeight: '700', color: COLORS.dark, marginBottom: '6px', letterSpacing: '0.3px' }}>💎 RAPPORT DE VALORISATION PROFESSIONNEL — 900€</div>
        <div style={{ fontSize: '13.5px', color: '#1a1a1a', lineHeight: '1.6', marginBottom: '8px' }}>
          Au-delà de l'estimation gratuite ci-dessus, recevez un <strong>rapport PDF 20 pages</strong> opposable :
        </div>
        <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: '12.5px', color: '#444', lineHeight: '1.7' }}>
          <li>3 méthodes M&A appliquées (DCF, Multiples sectoriels, ANR)</li>
          <li>Retraitements EBITDA, comparables transactions réelles</li>
          <li>Argumentaire chiffré utilisable face à banquier / repreneur / VC</li>
          <li>Livré sous 48h · Garantie satisfait ou remboursé 7j</li>
        </ul>
      </div>

      {/* CTAs : Stripe direct (paiement immédiat) + mailto (devis perso) */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {STRIPE_LINKS.valorisation ? (
          <a
            href={STRIPE_LINKS.valorisation}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-glow"
            onClick={() => {
              try {
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'begin_checkout', { currency: 'EUR', value: 900, items: [{ item_name: 'Valorisation', price: 900 }] });
                }
                if (typeof window.fbq === 'function') {
                  window.fbq('track', 'InitiateCheckout', { content_name: 'Valorisation', value: 900, currency: 'EUR' });
                }
              } catch (e) {}
            }}
            style={{
              flex: 1, minWidth: '240px',
              padding: '14px 18px', textAlign: 'center',
              background: `linear-gradient(135deg, ${COLORS.green} 0%, #A87A1C 100%)`,
              color: 'white', borderRadius: '10px', textDecoration: 'none',
              fontSize: '14px', fontWeight: '800',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              boxShadow: `0 4px 16px ${COLORS.green}55`,
            }}
          >
            💳 Commander ma valorisation pro — 900€ <ArrowRight size={14} />
          </a>
        ) : null}
        <button type="button" onClick={() => setShowAskForm(s => !s)} style={{
          flex: 1, minWidth: '180px',
          padding: '14px 18px', textAlign: 'center',
          background: 'white', color: COLORS.blue,
          border: `1.5px solid ${COLORS.blue}40`, borderRadius: '10px',
          fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
        }}>
          📧 Recevoir ma simulation par email
        </button>
      </div>

      {/* ── Formulaire d'envoi par email (apparaît au clic) ── */}
      {showAskForm && !askSent && (
        <div style={{ marginTop: '14px', padding: '16px 18px', background: `linear-gradient(135deg, #F4EFE4 0%, white 100%)`, border: `1px solid ${COLORS.blue}30`, borderRadius: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '800', color: '#0a1929', margin: '0 0 6px' }}>📨 Envoyez-moi cette simulation par email</h4>
          <p style={{ fontSize: '12.5px', color: '#5a6678', margin: '0 0 12px', lineHeight: '1.6' }}>
            Je vous envoie le détail complet de votre estimation ({fmtK(result.pondere.central)}) avec les 3 méthodes de calcul.
          </p>
          <form onSubmit={handleAskSubmit} style={{ display: 'grid', gap: '8px' }}>
            <input type="text" required value={askName} onChange={e => setAskName(e.target.value)} placeholder="Prénom" style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', background: 'white', boxSizing: 'border-box', width: '100%', outline: 'none' }} />
            <input type="email" required value={askEmail} onChange={e => setAskEmail(e.target.value)} placeholder="Email professionnel" style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', background: 'white', boxSizing: 'border-box', width: '100%', outline: 'none' }} />
            <button type="submit" disabled={askSubmitting} style={{ padding: '11px 14px', background: askSubmitting ? '#9CA3AF' : `linear-gradient(135deg, ${COLORS.blue} 0%, ${COLORS.dark} 100%)`, color: 'white', border: 'none', borderRadius: '8px', fontSize: '13.5px', fontWeight: '800', cursor: askSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {askSubmitting ? 'Envoi en cours…' : '📧 Recevoir ma simulation par email'}
            </button>
            {askError && <p style={{ fontSize: '11.5px', color: '#8B2C1C', margin: '4px 0 0', textAlign: 'center' }}>❌ {askError}</p>}
            <p style={{ fontSize: '10.5px', color: '#666', textAlign: 'center', margin: '4px 0 0' }}>🔒 RGPD · Désabonnement en 1 clic · Pas de spam</p>
          </form>
        </div>
      )}
      {askSent && (
        <div style={{ marginTop: '14px', padding: '16px 18px', background: COLORS.lightGreen, border: `1px solid ${COLORS.green}40`, borderRadius: '12px', textAlign: 'center' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#064e3b', margin: '0 0 4px' }}>✅ Simulation envoyée{askName ? `, ${askName.split(' ')[0]}` : ''} !</h4>
          <p style={{ fontSize: '12.5px', color: '#0A1929', lineHeight: '1.5', margin: 0 }}>
            Le détail va arriver dans <strong>{askEmail}</strong> dans les prochaines minutes. Vérifiez aussi vos spams.
          </p>
        </div>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// COMPOSANTS DE FORMULAIRE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Petit "ⓘ" cliquable avec bulle d'info au hover/click — pour expliquer le jargon financier
function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '6px', verticalAlign: 'middle' }}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen(!open); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        aria-label="Plus d'informations"
        style={{
          width: '17px', height: '17px', borderRadius: '50%',
          background: '#F4EFE4', color: COLORS.blue,
          border: 'none', cursor: 'help',
          fontSize: '11px', fontWeight: '700', lineHeight: '17px',
          padding: 0, fontFamily: 'inherit',
        }}
      >ⓘ</button>
      {open && (
        <span style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          background: '#0a1929', color: 'white',
          padding: '10px 14px', borderRadius: '8px',
          fontSize: '12.5px', fontWeight: '400', lineHeight: '1.55',
          width: '280px', maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          zIndex: 100, textAlign: 'left',
        }}>
          {text}
          <span style={{
            position: 'absolute', bottom: '-5px', left: '50%', transform: 'translateX(-50%) rotate(45deg)',
            width: '10px', height: '10px', background: '#0a1929',
          }} />
        </span>
      )}
    </span>
  );
}

function Field({ label, hint, info, children }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
        {label}
        {info && <InfoTooltip text={info} />}
      </label>
      {children}
      {hint && <div style={{ fontSize: '11.5px', color: '#5F6675', marginTop: '4px', display: 'flex', alignItems: 'flex-start', gap: '4px', lineHeight: 1.4 }}><Info size={11} style={{ marginTop: '2px', flexShrink: 0 }} /> {hint}</div>}
    </div>
  );
}

function SliderField({ label, value, min, max, step, format, onChange, hint, info }) {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  const ariaLabel = typeof label === 'string' ? label : undefined;
  const clamp = (v) => Math.min(max, Math.max(min, Number.isFinite(v) ? v : min));
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px', gap: '12px' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: COLORS.blue }}>
          {label}
          {info && <InfoTooltip text={info} />}
        </label>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '8px' }}>
          {format && <span style={{ fontSize: '13px', color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>{format(value)}</span>}
          {/* Champ éditable : saisie exacte au clavier (précision impossible au doigt sur un slider) */}
          <input
            type="number" inputMode="numeric" min={min} max={max} step={step} value={value}
            aria-label={ariaLabel ? `${ariaLabel} — saisir une valeur exacte` : 'Saisir une valeur exacte'}
            onChange={e => onChange(e.target.value === '' ? min : clamp(Number(e.target.value)))}
            style={{ width: '104px', textAlign: 'right', fontSize: '15px', fontWeight: '700', color: COLORS.blue, fontVariantNumeric: 'tabular-nums', fontFamily: 'inherit', border: '1px solid transparent', background: 'transparent', padding: '2px 4px', borderRadius: '2px' }}
          />
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        aria-label={ariaLabel}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: '100%', height: '6px', borderRadius: '3px', outline: 'none', cursor: 'pointer',
          background: `linear-gradient(to right, ${COLORS.blue} 0%, ${COLORS.blue} ${pct}%, #D8D3C5 ${pct}%, #D8D3C5 100%)`,
          WebkitAppearance: 'none', appearance: 'none',
        }} />
      {hint && <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '5px', display: 'flex', alignItems: 'flex-start', gap: '5px', lineHeight: 1.45 }}><Info size={12} style={{ marginTop: '2px', flexShrink: 0 }} aria-hidden="true" /> {hint}</div>}
    </div>
  );
}

const selectStyle = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #D8D3C5', borderRadius: '8px',
  fontSize: '14px', fontFamily: 'inherit', background: 'white', boxSizing: 'border-box', cursor: 'pointer',
};
